import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import { Button, Typography } from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { AnnouncementPanel, AnnouncementPanelHeader } from 'components/@common/AnnouncementPanel';
import { format, differenceInDays } from 'date-fns';
import { TFunction } from 'i18next';
import useEndOfYearProcessing from '../hooks/use-end-of-year-processing';
import { EndOfYearProcessingFormValues, EndOfYearProcessingNotificationType } from '../types';

interface EndOfYearProcessingAnnouncementProps {
  hideTypes?: EndOfYearProcessingNotificationType[];
}

type EndOfYearProcessingNotification = {
  title: ReactElement;
  summary: ReactElement;
  showCallToAction: boolean;
};

const DATE_FORMAT = 'MM/dd/yyyy';

const EndOfYearProcessingAnnouncement = ({ hideTypes }: EndOfYearProcessingAnnouncementProps) => {
  const { t } = useTranslation('EndOfYearProcessing');
  const { values, notificationType, openModal } = useEndOfYearProcessing();

  const show = notificationType && !hideTypes?.includes(notificationType);
  const notification =
    notificationType && endOfYearProcessingNotificationMap(values, t)[notificationType];

  let actions;
  if (notification?.showCallToAction) {
    actions = (
      <Button
        variant="contained"
        color="primary"
        disableElevation
        aria-label={t('Change Date')}
        startIcon={<CalendarTodayIcon />}
        onClick={openModal}
      >
        <span>{t('Change Date')}</span>
      </Button>
    );
  }

  return (
    <AnnouncementPanel
      show={show}
      Header={
        <AnnouncementPanelHeader>
          <Typography variant="h2">{notification?.title}</Typography>
        </AnnouncementPanelHeader>
      }
      Actions={actions}
    >
      <Typography variant="body1" color="textSecondary">
        {notification?.summary}
      </Typography>
    </AnnouncementPanel>
  );
};

const endOfYearProcessingNotificationMap: (
  values: EndOfYearProcessingFormValues,
  t: TFunction
) => {
  [key in EndOfYearProcessingNotificationType]: EndOfYearProcessingNotification;
} = (values, t) => ({
  [EndOfYearProcessingNotificationType.UPCOMING]: {
    title: (
      <>
        {values.dateTermEnd && (
          <>
            {t(
              `End of Year Processing will occur in ${differenceInDays(
                new Date(values.dateTermEnd),
                new Date()
              )} days on `
            )}
            <b>{format(new Date(values.dateTermEnd), DATE_FORMAT)}</b>.
          </>
        )}
      </>
    ),
    summary: (
      <>
        {t(
          `This process will automatically remove licenses and remove classes. Choose 'Change Date' to adjust timing.`
        )}
      </>
    ),
    showCallToAction: true
  },
  [EndOfYearProcessingNotificationType.PROCESSING_COMPLETE]: {
    title: <>{t(`End of Year Processing complete! Please request a set up date.`)}</>,
    summary: (
      <>
        {t(
          `You have not yet requested an implementation date for the new school year. Please select ‘Change Date’ to adjust timing.`
        )}
      </>
    ),
    showCallToAction: true
  },
  [EndOfYearProcessingNotificationType.BEFORE_NEXT_IMPLEMENTATION]: {
    title: (
      <>
        {values.upcomingDateTermStart && (
          <>
            {t(`End of Year Processing complete! Your requested set up date is `)}
            <b>{format(new Date(values.upcomingDateTermStart), DATE_FORMAT)}</b>.
          </>
        )}
      </>
    ),
    summary: (
      <>
        {t(
          `New school year implementation will create classes, if necessary, and license students and/or educators.  Choose ‘Change Date’ to adjust timing.`
        )}
      </>
    ),
    showCallToAction: true
  },
  [EndOfYearProcessingNotificationType.PROCESSING_FAILED]: {
    title: (
      <>
        {t(
          `We were unable to process your end of year processing. One of our onboarding specialists will contact you.`
        )}
      </>
    ),
    summary: <>{''}</>,
    showCallToAction: false
  },
  [EndOfYearProcessingNotificationType.NEW_YEAR_IMPLEMENTED]: {
    title: (
      <>{t('Thank you for getting your educators and students set up for the new school year!')}</>
    ),
    summary: (
      <>
        {t(
          'If you have any questions on your implementation, please contact our onboarding team here: link'
        )}
      </>
    ),
    showCallToAction: false
  }
});

export default withErrorBoundary(EndOfYearProcessingAnnouncement, {
  fallback: null
});
